/**
 * Signup funnel telemetry.
 *
 * Emits three events during the signup flow so the admin panel can
 * show a drop-off funnel:
 *   • otp_reached      — user opened the MSG91 widget
 *   • otp_submitted    — MSG91 fired onVerified (success) OR onError
 *                        (failed) after the user entered an OTP.
 *                        Also fired with status='failed' + error='abandoned'
 *                        when the user leaves the page without completing.
 *   • signup_completed — /auth/register-with-otp returned 200
 *
 * Events are correlated via a client-generated UUID stored in
 * sessionStorage so retries within a single visit dedupe cleanly.
 * The stored ID is cleared on signup_completed so a follow-up
 * failure gets a fresh session ID.
 *
 * Transports (in priority order):
 *   1. `navigator.sendBeacon` — best for pagehide/unload; queued by the
 *       browser and survives tab close.
 *   2. `fetch({ keepalive: true })` — fallback for browsers where
 *       sendBeacon returns false (payload too big, blocked by CSP).
 *   3. `api.post` — regular XHR path when the page is not unloading.
 *
 * All calls are best-effort — failures are swallowed so telemetry
 * NEVER blocks the real signup path.
 */
import { api, API } from './api';
import { BUILD_VERSION } from './buildVersion';

const SS_KEY = 'rb_signup_session_id';
const ENDPOINT = `${API}/analytics/signup-event`;

function _uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback for very old browsers.
  return 'sess-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

/** Ensures a session ID exists (idempotent). Returns the current ID. */
export function ensureSignupSessionId() {
  try {
    let sid = window.sessionStorage.getItem(SS_KEY);
    if (!sid) {
      sid = _uuid();
      window.sessionStorage.setItem(SS_KEY, sid);
    }
    return sid;
  } catch {
    return _uuid();
  }
}

export function clearSignupSessionId() {
  try { window.sessionStorage.removeItem(SS_KEY); } catch { /* noop */ }
}

/**
 * Fire-and-forget event emitter. Used for the two "in-flight" events
 * (otp_reached, signup_completed). Emits via axios so it works in
 * every browser we care about.
 *   emitSignupEvent('otp_reached', { mode: 'phone_only' })
 *   emitSignupEvent('otp_submitted', { mode, status: 'success' | 'failed', error })
 *   emitSignupEvent('signup_completed', { mode, user_id, mobile_e164 })
 */
export function emitSignupEvent(event_type, extra = {}) {
  const session_id = ensureSignupSessionId();
  const payload = { session_id, event_type, build_version: BUILD_VERSION, ...extra };
  // Never await, never throw — telemetry MUST NOT block signup.
  api.post('/analytics/signup-event', payload).catch(() => {});
}

/**
 * Beacon-safe emitter. Prefers `navigator.sendBeacon` so the event
 * survives page unload; falls back to `fetch({ keepalive: true })`
 * for browsers that can't beacon a JSON payload. Used from pagehide
 * / visibilitychange handlers to record "user abandoned OTP flow".
 *
 * Returns true if the event was dispatched (best-effort — we can't
 * know if the network accepted it), false if we couldn't queue it.
 */
export function emitSignupEventBeacon(event_type, extra = {}) {
  const session_id = ensureSignupSessionId();
  const payload = { session_id, event_type, build_version: BUILD_VERSION, ...extra };
  const body = JSON.stringify(payload);
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // sendBeacon uses Content-Type: application/json when we pass a
      // Blob with the right type. Some browsers still send it as
      // text/plain — the FastAPI endpoint accepts both because the
      // pydantic model just parses the JSON body.
      const blob = new Blob([body], { type: 'application/json' });
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return true;
    }
  } catch { /* fall through */ }
  try {
    // keepalive lets fetch outlive the current document.
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      // No credentials — this is anonymous telemetry, and cookies
      // aren't required by the backend endpoint.
      credentials: 'omit',
      mode: 'cors',
    }).catch(() => {});
    return true;
  } catch { return false; }
}
